import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import FlexBetween from 'components/FlexBetween';
import { DAY_LABELS } from './constants';

const RouteSidebar = ({
  routes,
  selectedRoute,
  onSelectRoute,
  selectedRouteFlights,
  acTypeColorMap,
  selectedSeasonId,
}) => {
  const theme = useTheme();

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}>
      <Box px={1.5} py={1} borderBottom={`1px solid ${theme.palette.divider}`}>
        <Typography variant="subtitle2">
          {selectedRoute
            ? `${selectedRoute} - ${selectedRouteFlights.length} pattern${selectedRouteFlights.length !== 1 ? 's' : ''}`
            : 'Routes'}
        </Typography>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {selectedRoute ? (
          // Flight detail for selected route
          <Box p={1}>
            <Chip
              label="← All routes"
              size="small"
              onClick={() => onSelectRoute(null)}
              sx={{ mb: 1, cursor: 'pointer' }}
            />
            {selectedRouteFlights.map((f) => (
              <Box
                key={f._id}
                p={1} mb={0.5} borderRadius={1}
                border={`1px solid ${theme.palette.divider}`}
              >
                <FlexBetween>
                  <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
                    {f.carrier}{f.flightNo}
                  </Typography>
                  <Chip
                    label={f.acType || '-'}
                    size="small"
                    sx={{ bgcolor: acTypeColorMap[f.acType] || '#607d8b', color: '#fff', fontSize: '0.6rem' }}
                  />
                </FlexBetween>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {f.std} → {f.sta}{f.nextDay ? `+${f.nextDay}` : ''}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.3} mt={0.5}>
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                    <Chip
                      key={d}
                      label={DAY_LABELS[d][0]}
                      size="small"
                      variant={f.daysOfOperation?.includes(String(d)) ? 'filled' : 'outlined'}
                      color={f.daysOfOperation?.includes(String(d)) ? 'primary' : 'default'}
                      sx={{ minWidth: 24, px: 0, fontSize: '0.6rem' }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          // Full route list
          <List dense disablePadding>
            {routes.map((r) => (
              <ListItemButton
                key={r.key}
                selected={selectedRoute === r.key}
                onClick={() => onSelectRoute(r.key === selectedRoute ? null : r.key)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={
                    <FlexBetween>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                        {r.dep} → {r.arr}
                      </Typography>
                      <Chip label={`${r.totalFreq}x`} size="small" variant="outlined" />
                    </FlexBetween>
                  }
                  secondary={
                    <Box display="flex" gap={0.5} mt={0.3}>
                      {r.sectorType && (
                        <Chip
                          label={r.sectorType}
                          size="small"
                          variant="outlined"
                          color={r.sectorType === 'INT' ? 'info' : 'default'}
                          sx={{ fontSize: '0.6rem' }}
                        />
                      )}
                      {[...r.acTypes].filter(Boolean).map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          sx={{ bgcolor: acTypeColorMap[t] || '#607d8b', color: '#fff', fontSize: '0.6rem' }}
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
            {routes.length === 0 && (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  {selectedSeasonId ? 'No routes found' : 'Select a season'}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default RouteSidebar;
